import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, inject, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
  ManageTableBudgetColumn,
  ManageTableBudgetColumnName,
  ManageTableTotalValues
} from '@manage-ceg/types/manage-ceg-page.types';
import { BehaviorSubject, finalize, map, Observable, Subject, takeUntil } from 'rxjs';
import { BudgetAllocationCellGesturesEvent } from 'app/budget-allocation/components/budget-allocation-cell/budget-allocation-cell.types';
import { messages } from 'app/budget-object-details/messages';
import { BudgetAllocationsTableEvent, ActualSpendsData } from '../../budget-allocations-table/budget-allocations-table.type';
import { TableContentShadowsBaseComponent } from '@shared/directives/TableContentShadowsBase';
import { CampaignDetailsService } from '../../../services/campaign-details.service';
import { MatDialog } from '@angular/material/dialog';
import { BudgetAllocationService } from '../../../../shared/services/backend/budget-allocation.service';
import { LocalStorageService } from '../../../../../../../common-lib/src/services/local-storage.service';
import { Configuration } from '../../../../app.constants';
import { EditBudgetModalService } from '../../../../shared/services/edit-budget-modal.service';
import { BudgetService } from '../../../../shared/services/backend/budget.service';
import { Budget } from '../../../../shared/types/budget.interface';
import { TableColumnId } from '../../../../manage-ceg-table/components/manage-ceg-table-container/manage-ceg-table-container.enum';
export interface TableAllocationRow {
  allocationId: number;
  timeframeId: number;
  shortName: string;
  locked: boolean;
  values: ManageTableTotalValues,
}

export interface TableAllocationsData {
  rows: TableAllocationRow[];
  totals: ManageTableTotalValues;
}

export interface OpenCellDetailsEvent {
  timeframeId?: number;
}

@Component({
  selector: 'ceg-budget-allocations-table',
  templateUrl: './ceg-budget-allocations-table.component.html',
  styleUrls: ['./ceg-budget-allocations-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CegBudgetAllocationsTableComponent extends TableContentShadowsBaseComponent implements OnInit, OnDestroy {
  private readonly dialog = inject(MatDialog);
  protected restrictedFromDrop: Record<string, boolean> = {};
  public updateShadows$ = new BehaviorSubject(null);
  protected manageTableBudgetColumnName = ManageTableBudgetColumnName;
  protected segmentlessAllocationTooltip = messages.SEGMENTLESS_ALLOCATION_CEG_TOOLTIP;
  dragFromRow: TableAllocationRow;
  private allocationDialogShowed = false;
  @Input() tableRows: TableAllocationRow[];
  @Input() tableColumns: ManageTableBudgetColumn[];
  @Input() allocationTotals: Partial<ManageTableTotalValues>;
  @Input() campaignIsClosed: boolean;
  @Input() isReadOnlyMode: boolean;
  @Input() isSegmentlessCampaign: boolean;
  @Input() objectId: number;
  @Input() externalIntegrationType: { isExternal: boolean, integrationName: string };
  @Output() openExpenses = new EventEmitter<OpenCellDetailsEvent>();
  @Output() openBudgetAllocationDetails = new EventEmitter<OpenCellDetailsEvent>();
  @Output() onAllocationChange = new EventEmitter<BudgetAllocationsTableEvent[]>();
  @Output() onForecastAmountChange = new EventEmitter<BudgetAllocationsTableEvent[]>();
  @HostBinding('class.sticky-footer') footerIsSticky = true;
  @Input() objectType: string;  
  @Input() companyId: number;
  @Input() budget: Budget;
  @Input() isForecastEnabled = false;
  @ViewChild('budgetAllocationCell') budgetAllocationCell: ElementRef;

  selectedCell: { row: number, column: number } | null = null;
  selectedColumn: string;
  selectedRowTimeframe : number;
  selectedRowValue : number;
  public readonly destroy$ = new Subject<void>();
  private isBudgetEditModalOpen: boolean = false;
  
  clearSelection() {
    this.selectedCell = null;
  }

  iconByObjectType = {};
  accData: ActualSpendsData[];
  isLoading = false;
  isForecastSmartCell = false;
  
  private dragActiveKey: ManageTableBudgetColumnName;
  
  constructor(private cdRef: ChangeDetectorRef,
    private budgetAllocationervice: BudgetAllocationService,
    private readonly configuration : Configuration,
    private focusHandler: EditBudgetModalService,
    private budgetService : BudgetService
  ) {
      super();
  }

  ngOnInit(): void {

    this.iconByObjectType = {
      ['Campaigns']: ['far', 'rocket-launch'],
      ['ExpenseGroups']: ['fas', 'briefcase'],
      ['Expenses']: ['fas', 'coins'],
      ['ChildCampaigns']: ['far', 'rocket-launch'],
    };
    
    this.isForecastSmartCell = this.budget?.anchor_field === TableColumnId.Forecast;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['budget']) {
      this.isForecastSmartCell = this.budget?.anchor_field === TableColumnId.Forecast;
    }
  }

  // which column is primary (rich/editable)
  get isBudgetPrimary(): boolean { 
    return !this.isForecastSmartCell; 
  }
  get isForecastPrimary(): boolean { 
    return this.isForecastSmartCell; 
  }

  // shared guards
  isCellDisabled = (row: TableAllocationRow) =>
    this.campaignIsClosed || row.locked;

  isCellEditable = (row: TableAllocationRow) =>
    !this.isReadOnlyMode && !this.isSegmentlessCampaign && !this.isCellDisabled(row);


  highlightCell(row: number, column: number, selectedRow: TableAllocationRow, col: ManageTableBudgetColumn) {
      if(col.id === 'budget' || col.id === 'forecast' || this.objectType === 'Program') {
        return false;
      }
      if(selectedRow.values[col.id]){
        this.selectedColumn = col.id;
        this.accData = null;
        this.selectedRowTimeframe = selectedRow.timeframeId;
        this.selectedRowValue = selectedRow.values[col.id];
        if(this.selectedCell && this.selectedCell.row === row && this.selectedCell.column === column){
          this.selectedCell = null;
        }
        else{
          this.selectedCell = { row: row, column: column };
          this.isLoading = true;
          if(col.id === 'actual'){
            this.getActualSpends$(this.objectId, selectedRow.timeframeId, this.companyId, this.budget?.id).pipe(
              takeUntil(this.destroy$),
              finalize(() => {
                this.isLoading = false;
                this.cdRef.detectChanges(); // Manually trigger change detection
              })
            ).subscribe(data => {
              this.accData = data;
            });
          }
          else{
            let isCommit = false;
            if(col.id === 'committed'){
              isCommit = true;
            }
            this.getRemainingAmount$(this.objectId, selectedRow.timeframeId, this.companyId, this.budget?.id, isCommit).pipe(
              takeUntil(this.destroy$),
              finalize(() => {
                this.isLoading = false;
                this.cdRef.detectChanges(); // Manually trigger change detection
              })
            ).subscribe(data => {
              this.accData = data;
            });
          }
          
        }
      }
    }

  

  getActualSpends$(campaignId: number, periodId: number, companyId : number, budgetId : number): Observable<any> {
    const isGoal = this.objectType === 'Goal';
    return this.budgetAllocationervice.getExpenses(campaignId, periodId, companyId, budgetId, isGoal);
  }

  getRemainingAmount$(campaignId: number, allocationId: number, companyId : number, budgetId : number, isCommit : boolean): Observable<any> {
    const isGoal = this.objectType === 'Goal';
    return this.budgetAllocationervice.getRemainingAmount(campaignId, allocationId, companyId, budgetId, isGoal, isCommit);
  }

  public handleTableResize(): void {
    setTimeout(() => {
      this.updateShadows$.next(null);
    }, 700)
  }

  

  public handleAllocationChange(amount: number, row: TableAllocationRow): void {
    const prevValue = row.values[ManageTableBudgetColumnName.Budget];

    if (prevValue !== amount) {
      this.onAllocationChange.emit([{
        amount,
        allocationId: row.allocationId,
        budgetTimeframeId: row.timeframeId,
        isManual: true
      }]);
    }
  }

  public handleForecastAmountChange(amount: number, row: TableAllocationRow): void {
    const prevValue = row.values[ManageTableBudgetColumnName.Forecast];

    if (prevValue !== amount) {
      this.onForecastAmountChange.emit([{
        amount,
        allocationId: row.allocationId,
        budgetTimeframeId: row.timeframeId
      }]);
    }
  }
  

  public handleDoubleClick($event: BudgetAllocationCellGesturesEvent, row: TableAllocationRow): void {
    this.getTotalSpend(row).pipe(takeUntil(this.destroy$)).subscribe(totalSpend => {
      this.onAllocationChange.emit([{
        amount: totalSpend,
        allocationId: row.allocationId,
        budgetTimeframeId: row.timeframeId
      }]);
    });
  }

  handleForecastDoubleClick(event: any, row: TableAllocationRow) {
    this.getTotalSpend(row).pipe(takeUntil(this.destroy$)).subscribe(totalSpend => {
      this.onForecastAmountChange.emit([{
        amount: totalSpend,
        allocationId: row.allocationId,
        budgetTimeframeId: row.timeframeId
      }]);
    });    
  }
  
  private get primaryKey(): ManageTableBudgetColumnName {
    return this.isForecastSmartCell
      ? ManageTableBudgetColumnName.Forecast
      : ManageTableBudgetColumnName.Budget;
  }

  protected handleOnDragStart($event: BudgetAllocationCellGesturesEvent, row: TableAllocationRow): void {
    this.dragFromRow = row;
    // remember which column was active when drag began
    this.dragActiveKey = this.primaryKey;
  }

  public handleOnDrop($event: BudgetAllocationCellGesturesEvent, row: TableAllocationRow): void {
    const activeKey = this.dragActiveKey ?? this.primaryKey;

    this.getTotalSpend(this.dragFromRow).pipe(takeUntil(this.destroy$)).subscribe(startRowTotalSpendAmount => {
      let startRowUpdatedAmount = 0;
      let endRowUpdatedAmount = 0;

      const startRowAvailableAmount = this.dragFromRow.values[ManageTableBudgetColumnName.Available] ?? 0;
      const endRowAvailableAmount   = row.values[ManageTableBudgetColumnName.Available] ?? 0;
      const endRowCurrentAmount     = row.values[activeKey] ?? 0;

      if (endRowAvailableAmount < 0) {
        const amountDiff = Math.min(startRowAvailableAmount, Math.abs(endRowAvailableAmount));
        endRowUpdatedAmount   = endRowCurrentAmount + amountDiff;
        startRowUpdatedAmount = startRowTotalSpendAmount + startRowAvailableAmount - amountDiff;
      } else {
        endRowUpdatedAmount   = endRowCurrentAmount + startRowAvailableAmount;
        startRowUpdatedAmount = startRowTotalSpendAmount;
      }

      const payload = [
        {
          amount: startRowUpdatedAmount,
          allocationId: this.dragFromRow.allocationId,
          budgetTimeframeId: this.dragFromRow.timeframeId
        },
        {
          amount: endRowUpdatedAmount,
          allocationId: row.allocationId,
          budgetTimeframeId: row.timeframeId
        }
      ];

      if (activeKey === ManageTableBudgetColumnName.Budget) {
        this.onAllocationChange.emit(payload);
      } else {
        this.onForecastAmountChange.emit(payload);
      }
    });
  }
  
  private getTotalSpend(row: TableAllocationRow): Observable<number> {
    return this.budgetService.getBudgetsCABFormula(this.companyId, this.budget?.id).pipe(
      map((res) => {
        const isCABFormulaApplied = res.some(item => item.budget !== null);
  
        if (isCABFormulaApplied) {
          const fields = res.slice(1).map(item => item.manage_page_fields);
  
          const fieldMap = {
            2: ManageTableBudgetColumnName.Forecast,
            3: ManageTableBudgetColumnName.Actual,
            4: ManageTableBudgetColumnName.Committed,
            5: ManageTableBudgetColumnName.Planned
          };
  
          return fields.reduce((sum, field) => {
            const key = fieldMap[field];
            return key && row.values[key] != null ? sum + row.values[key] : sum;
          }, 0);
        } else {
          return row.values[ManageTableBudgetColumnName.Actual] +
                 (row.values[ManageTableBudgetColumnName.Planned] || 0) +
                 (row.values[ManageTableBudgetColumnName.Committed] || 0);
        }
      })
    );
  }  
  

  public onCellClick(timeframeId: number, columnId: ManageTableBudgetColumnName): void {
    if (!this.objectId) {
      return;
    }

    if (columnId === ManageTableBudgetColumnName.Actual) {
      this.openExpenses.emit({ timeframeId });
    } else if (columnId === ManageTableBudgetColumnName.Committed || columnId === ManageTableBudgetColumnName.Planned) {
      this.openBudgetAllocationDetails.emit({ timeframeId });
    }
  }

  public handleOnFocus(event: boolean, row: TableAllocationRow): void {
   
   const showManualChangeAllocationFlag = this.externalIntegrationType?.isExternal && event && !this.allocationDialogShowed;  
    const showIntegrationArtefactManualChangeConfirmationHandler = (cb ?: () => void) => {
      if (showManualChangeAllocationFlag) {
        this.allocationDialogShowed = true;
        CampaignDetailsService.showManualChangeConfirmDialog(this.externalIntegrationType.integrationName, this.dialog, cb);
      }
    }

    const budgetPopupKey = this.configuration.EDIT_TOTAL_BUDGET_POPUP_KEY_FOR_FORECAST;
    const dontShowBudgetEditPopup = LocalStorageService.getFromStorage(budgetPopupKey) || false;


    if(!this.isForecastEnabled || row.values[ManageTableBudgetColumnName.Budget] <= 0 || dontShowBudgetEditPopup){
      showIntegrationArtefactManualChangeConfirmationHandler();
    }
    else {
      this.focusHandler.handleOnFocus(event, row.values[ManageTableBudgetColumnName.Budget], this.isForecastEnabled, document.activeElement as HTMLInputElement,  showManualChangeAllocationFlag ? showIntegrationArtefactManualChangeConfirmationHandler: null);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
